import { useTranslation } from "react-i18next"
import { HighlightedText } from "@/components/highlighted-text"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent } from "@/components/ui/search"
import { joinStrings } from "@/lib/joinString"
import {
  ChMedicationSearchState,
  MedicationItem,
} from "@/modules/patients/detail/medication/components/ch-medication-search"
import { FreeTextMedicationCreation } from "@/modules/patients/detail/medication/components/free-text-medication-creation"

type ChMedicationSearchResultWithTemplateProps = ChMedicationSearchState & {
  searchResultsRef: React.RefObject<HTMLDivElement | null>
  searchValue: string
  handleSelectFreeTextSearch: VoidFunction
  handleSelectMedication: (medication: MedicationItem) => void
  handleHighlightContent: (
    defaultValues: { name: string; mainSubstance: string | null },
    highlights: ReadonlyArray<{ readonly key: string; readonly value: string }> | null,
  ) => { title: string; isHighlight: boolean }
}

export const ChMedicationSearchResultWithoutTemplate = (props: ChMedicationSearchResultWithTemplateProps) => {
  const {
    searchResultStatus,
    data,
    searchResultsRef,
    searchValue,
    handleSelectMedication,
    handleSelectFreeTextSearch,
    handleHighlightContent,
  } = props

  const { t } = useTranslation()

  return (
    <>
      {searchResultStatus === "noResults" && (
        <SearchContent ref={searchResultsRef}>
          <FreeTextMedicationCreation searchTerm={searchValue} onClick={handleSelectFreeTextSearch} />
        </SearchContent>
      )}
      {searchResultStatus === "hasResults" && data && (
        <SearchContent ref={searchResultsRef} data-testid="medication-search-results">
          <ScrollArea className="py-2">
            <div role="group">
              <div className="text-on-surface-subdued overflow-hidden p-2 text-sm font-medium">
                {t("patient.medication.allProductsAndSubstances")}
              </div>
              {data.map((medication) => {
                const informationText = joinStrings(
                  [
                    medication.packageSizeDescription,
                    medication.formattedPrice,
                    medication.dispensingCategory,
                    medication.refundCategory,
                  ].filter((f) => f),
                )
                const { title, isHighlight } = handleHighlightContent(medication, medication.highlights)
                return (
                  <div
                    key={medication.id}
                    data-testid="medication-search-item"
                    onClick={() => handleSelectMedication(medication)}
                    className="aria-selected:bg-surface-hovered hover:bg-surface-hovered active:bg-surface-subdued-pressed relative flex cursor-default flex-col rounded-md px-2 py-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50">
                    {isHighlight ? (
                      <PotentiallyUnsafeInnerHtml value={title} />
                    ) : (
                      <HighlightedText
                        className="text-on-surface truncate text-sm"
                        title={title}
                        text={title}
                        searchValue={searchValue}
                        extendedMarkClasses="font-normal"
                      />
                    )}
                    <span className="text-on-surface-subdued text-xs">{informationText}</span>
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        </SearchContent>
      )}
    </>
  )
}
